import * as React from 'react'
import { useCallback } from 'react'

import { IUploadLog, SourceEntityType } from '~/client/graph'
import ListTagCell, {
  TagCellItemKeyEnum,
} from '~/client/src/desktop/components/ListTagCell'
import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import AttachmentsStore, {
  getFileTypeToCategory,
} from '../../../Attachments.store'
import AttachmentFileIcon from '../../AttachmentFileIcon'

interface IProps {
  attachment: IUploadLog
  fileName: string
  fileType: string
  store: AttachmentsStore
  projectDateStore: ProjectDateStore
  projectMembersStore: ProjectMembersStore
  companiesStore: CompaniesStore
  locationAttributesStore: LocationAttributesStore
  workflowTypesStore: WorkflowTypesStore
  userProjectsStore: UserProjectsStore
  workflowCategoriesStore: WorkflowCategoriesStore
  workflows?: Workflow[]
}

const AttachmentModalFileDetails: React.FC<IProps> = ({
  attachment,
  fileName,
  fileType,
  store,
  projectDateStore,
  projectMembersStore,
  companiesStore,
  locationAttributesStore,
  workflowTypesStore,
  userProjectsStore,
  workflowCategoriesStore,
}) => {
  const workflow = store.workflows.get(attachment.sourceEntityId)
  const renderDetailRow = (
    label: string,
    value: React.ReactNode = '-',
  ): JSX.Element => {
    return (
      <div className="px24 py12">
        <div className="text primary mb4">{label}</div>
        <div className="text extra-large">{value}</div>
      </div>
    )
  }

  const renderSeparator = (): JSX.Element => {
    return (
      <div className="px24 py8">
        <div className="bb-light-input-border" />
      </div>
    )
  }

  const handleWorkflowClick = useCallback((): void => {
    const { sourceEntityType, sourceEntityId } = attachment

    if (sourceEntityType === SourceEntityType.Form) {
      store.openFormDetailsPanel(store.workflows.get(sourceEntityId))
    } else if (sourceEntityType === SourceEntityType.Delivery) {
      store.openDeliveryDetailsPanel(sourceEntityId)
    }
  }, [attachment, store])

  const getSourceDisplay = (): React.ReactNode => {
    const sourceText = store.getSourceDisplay(attachment)

    return (
      <div
        className="text large underline pointer"
        onClick={handleWorkflowClick}
      >
        {sourceText}
      </div>
    )
  }

  const getWorkflowTypeDisplay = (): JSX.Element => {
    const { sourceEntityType, sourceEntityTypeName } = attachment

    if (sourceEntityType === SourceEntityType.Form) {
      return (
        <div className="row y-center">
          <WorkflowTypeIcon
            iconName={workflow?.getIconNameOfPermitType(workflowTypesStore)}
            size="small"
            className="tw:mr-1"
          />
          <div className="text large">
            {workflow?.getNameOfPermitType(workflowTypesStore) ||
              sourceEntityTypeName ||
              sourceEntityType}
          </div>
        </div>
      )
    }
    return <div className="text large">{sourceEntityType}</div>
  }

  const getFormTypeDisplay = (): React.ReactNode => {
    const { sourceEntityType, sourceEntityTypeName } = attachment
    const category = workflow?.getCategoryCaption(workflowCategoriesStore)

    if (sourceEntityType === SourceEntityType.Form) {
      return (
        <div className="row y-center">
          <span>{category || sourceEntityTypeName || sourceEntityType}</span>
        </div>
      )
    }

    return (
      <div className="row y-center">
        <span>{sourceEntityType}</span>
      </div>
    )
  }

  const getLocationDisplay = (): React.ReactNode => {
    const { locationName, locationIds, sourceEntityId } = attachment
    const workflow = store.workflows.get(sourceEntityId)
    const workflowLocations = workflow?.locations || []

    const hasWorkflowLocations = workflowLocations.length > 0
    const hasLocationIds = locationIds && locationIds.length > 0

    if (!hasWorkflowLocations && !hasLocationIds && !!locationName) {
      return (
        <SitemapAttributeTag
          shouldShowAsTag={false}
          contentContainerClassName="text-ellipsis py2 row name-cell"
          shouldUseAlternativeIcon
        >
          {locationName}
        </SitemapAttributeTag>
      )
    }

    if (!hasWorkflowLocations && !hasLocationIds) {
      const { projectLocation } = locationAttributesStore
      return (
        <SitemapAttributeTag
          shouldShowAsTag={false}
          contentContainerClassName="text-ellipsis py2 row name-cell"
          dataObject={projectLocation}
          shouldUseAlternativeIcon
        >
          {Localization.translator.project}
        </SitemapAttributeTag>
      )
    }

    // By id: the kind stored beside it on the workflow value or the upload is a
    // snapshot that goes stale on a re-kind (STX1-597)
    const locations = hasWorkflowLocations
      ? workflowLocations
          .map(locationObj => locationAttributesStore.getById(locationObj.id))
          .filter(Boolean)
      : locationIds
          ?.map(id => locationAttributesStore.getById(id))
          .filter(Boolean) || []

    return (
      <ListTagCell
        items={locations}
        renderItemKey={TagCellItemKeyEnum.LOCATION}
      />
    )
  }

  const getStatusDisplay = (): React.ReactNode => {
    if (!workflow) {
      return '-'
    }
    return (
      <StatusBadge
        status={workflow.status}
        workflowStepLevel={workflow.workflowStepLevel}
        isLate={workflow.isLate}
      />
    )
  }

  const getDateUploadedDisplay = (): string => {
    const { getMonthDayYearAndTimeToDisplay } = projectDateStore
    return getMonthDayYearAndTimeToDisplay(attachment.createdAt)
  }

  const getUploadedByDisplay = (): React.ReactNode => {
    const { getById } = projectMembersStore
    const { userId, userName } = attachment

    const user = getById(userId)

    if (!user) {
      return userName || '-'
    }

    return (
      <UserProfilePreview
        user={user}
        showBoldName={false}
        isUserNameClickable
        isAlwaysUnderlined
        isBriefInfoHidden
      />
    )
  }

  const getCompanyDisplay = (): React.ReactNode => {
    const { getCompanyById } = companiesStore
    const { userCompanyId, userCompanyName, userId } = attachment

    const user = projectMembersStore.getById(userId)
    const userProject = user && userProjectsStore.getByUser(user)
    const currentUserCompanyId = userProject?.companyId || userCompanyId
    const company = getCompanyById(currentUserCompanyId)

    if (!company) {
      return userCompanyName || '-'
    }

    return (
      <CompanyProfilePreview
        company={company}
        shouldShowCompanyType={false}
        isMenuHidden
        isPhoneHidden
        contentContainerClassName="underline"
      />
    )
  }

  const fileTypeText = getAttachmentFileType(fileType, '/', true)
  const fileCategoryText = getFileTypeToCategory(fileType)
  const workflowTitle = workflow?.title || '-'

  return (
    <div className="attachment-modal-details-content p20">
      <div className="col">
        <div className="row y-center x-center pt40 pb20">
          <AttachmentFileIcon
            fileName={fileType}
            className="row x-center y-center attachment-details-icon-holder preview-icon"
            separator="/"
          />
        </div>

        <div className="col">
          {renderDetailRow(`${Localization.translator.fileName}:`, fileName)}
          {renderDetailRow(
            `${Localization.translator.fileCategory}:`,
            fileCategoryText,
          )}
          {renderDetailRow(
            `${Localization.translator.fileType}:`,
            fileTypeText.toUpperCase(),
          )}
          {renderSeparator()}
          {renderDetailRow(
            `${Localization.translator.workflowCategory}:`,
            getFormTypeDisplay(),
          )}
          {renderDetailRow(
            `${Localization.translator.workflowType}:`,
            getWorkflowTypeDisplay(),
          )}
          {renderDetailRow(
            `${Localization.translator.workflowId}:`,
            getSourceDisplay(),
          )}
          {renderDetailRow(
            `${Localization.translator.workflowTitle}:`,
            workflowTitle,
          )}
          {renderDetailRow(
            `${Localization.translator.status}:`,
            getStatusDisplay(),
          )}
          {renderDetailRow(
            `${Localization.translator.location}:`,
            getLocationDisplay(),
          )}
          {renderSeparator()}
          {renderDetailRow(
            `${Localization.translator.company}:`,
            getCompanyDisplay(),
          )}
          {renderDetailRow(
            `${Localization.translator.uploadedBy}:`,
            getUploadedByDisplay(),
          )}
          {renderDetailRow(
            `${Localization.translator.dateUploaded}:`,
            getDateUploadedDisplay(),
          )}
        </div>
      </div>
    </div>
  )
}

export default AttachmentModalFileDetails
